import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar, PieChart, Pie, Cell, Legend, LineChart, Line,
} from "recharts";
import {
  Wallet, Percent, TrendingUp, TrendingDown, BedDouble, LogIn, LogOut,
  AlertTriangle, ShieldCheck, GitBranch, Sparkles, Clock, RefreshCw,
  Activity, Award, LayoutDashboard, DollarSign,
} from "lucide-react";
import api from "@/lib/api";
import {
  PageHeader, Card, Metric, Badge, SectionTitle, Section, EmptyState, Skeleton,
} from "@/components/primitives";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

/* ── Chart palette — approved HOCP tokens ────────────────────────────── */
const CHART = {
  primary: "#132F45",
  orange:  "#EA8B33",
  gold:    "#F3B940",
  steel:   "#32667F",
  success: "#34A853",
  warn:    "#EA8600",
  error:   "#D93025",
  info:    "#4E8BC2",
  neutral: "#7C8895",
};
const SERIES = [CHART.primary, CHART.orange, CHART.gold, CHART.steel, CHART.info,
                 CHART.success, CHART.warn, CHART.neutral];

/* ── Formatters ─────────────────────────────────────────────────────── */
const fmtMoney = (minor) => {
  const v = Number(minor || 0) / 100;
  return v.toLocaleString(undefined, { style: "currency", currency: "INR",
                                        maximumFractionDigits: 0 });
};
const fmtMoneyShort = (minor) => {
  const v = Number(minor || 0) / 100;
  if (Math.abs(v) >= 1e7) return `₹${(v / 1e7).toFixed(1)}Cr`;
  if (Math.abs(v) >= 1e5) return `₹${(v / 1e5).toFixed(1)}L`;
  if (Math.abs(v) >= 1e3) return `₹${(v / 1e3).toFixed(1)}K`;
  return `₹${v.toFixed(0)}`;
};
const fmtNum = (v) => Number(v || 0).toLocaleString();
const fmtSecs = (s) => {
  const n = Number(s || 0);
  if (!n) return "—";
  if (n < 60)   return `${n}s`;
  if (n < 3600) return `${(n/60).toFixed(0)}m`;
  return `${(n/3600).toFixed(1)}h`;
};

/* ── Skeleton wrapper ───────────────────────────────────────────────── */
function KpiSkeleton({ count = 4 }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="hocp-card p-5">
          <Skeleton className="h-3 w-24 mb-4" />
          <Skeleton className="h-7 w-32 mb-3" />
          <Skeleton className="h-3 w-20" />
        </div>
      ))}
    </div>
  );
}

/* ── Tooltip skin ───────────────────────────────────────────────────── */
function ChartTooltip({ active, payload, label, moneyMode = true }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-hocp-alice bg-white shadow-lg px-3 py-2 text-[12px]"
         data-testid="chart-tooltip">
      {label && <div className="font-semibold text-hocp-textPrimary mb-1">{label}</div>}
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2 tabular-nums">
          <span className="w-2 h-2 rounded-full" style={{ background: p.color || p.fill }} />
          <span className="text-hocp-textSecondary">{p.name}:</span>
          <span className="font-medium text-hocp-textPrimary">
            {moneyMode ? fmtMoneyShort(p.value) : fmtNum(p.value)}
          </span>
        </div>
      ))}
    </div>
  );
}

/* ── Main page ─────────────────────────────────────────────────────── */
export default function ExecutiveDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [rebuilding, setRebuilding] = useState(false);
  const [executive, setExecutive] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [finance, setFinance] = useState(null);
  const [frontOffice, setFrontOffice] = useState(null);
  const [workflows, setWorkflows] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const [e, r, f, fo, w] = await Promise.all([
        api.get("/dashboard/executive"),
        api.get("/dashboard/revenue?days=14"),
        api.get("/dashboard/finance?days=14"),
        api.get("/dashboard/front-office"),
        api.get("/dashboard/workflows"),
      ]);
      setExecutive(e.data); setRevenue(r.data); setFinance(f.data);
      setFrontOffice(fo.data); setWorkflows(w.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  const rebuild = async () => {
    setRebuilding(true);
    try { await api.post("/dashboard/rebuild"); await refresh(); }
    finally { setRebuilding(false); }
  };

  /* ── Derived chart data ───────────────────────────────────────── */
  const revenueTrend = useMemo(() => (revenue?.daily_trend || []).map(d => ({
    day: d.business_day?.slice(5),
    revenue: d.revenue_net_minor,
    tax: d.tax_total_minor,
    refunds: d.refunds_total_minor,
    discount: d.discount_total_minor,
  })), [revenue]);

  const cashTrend = useMemo(() => (finance?.cash_daily || []).map(d => ({
    day: d.business_day?.slice(5),
    collected: d.cash_collected_minor,
    refunded: d.cash_refunded_minor,
  })), [finance]);

  const dept = useMemo(() => Object.entries(revenue?.by_department || {})
    .map(([k, v]) => ({ name: k, value: v }))
    .filter(x => x.value > 0)
    .sort((a, b) => b.value - a.value), [revenue]);

  const centre = useMemo(() => Object.entries(revenue?.by_revenue_centre || {})
    .map(([k, v]) => ({ name: k, value: v }))
    .filter(x => x.value > 0)
    .sort((a, b) => b.value - a.value), [revenue]);

  const paymentMix = useMemo(() => Object.entries(finance?.cash_by_method || {})
    .map(([k, v]) => ({ name: k, value: v }))
    .filter(x => x.value > 0), [finance]);

  const roomStatus = useMemo(() => {
    const rs = frontOffice?.room_status_summary || {};
    return [
      { name: "Occupied",     value: rs.occupied     || 0, color: CHART.primary },
      { name: "Vacant Clean", value: rs.vacant_clean || 0, color: CHART.success },
      { name: "Vacant Dirty", value: rs.vacant_dirty || 0, color: CHART.warn },
      { name: "Out of Order", value: rs.out_of_order || 0, color: CHART.error },
    ].filter(x => x.value > 0);
  }, [frontOffice]);

  const workflowMetrics = useMemo(() => ([
    { label: "Pending Approvals",   value: workflows?.pending_total || 0 },
    { label: "SLA Breach %",        value: workflows?.sla_breach_pct || 0, suffix: "%" },
    { label: "Escalation %",        value: workflows?.escalation_pct || 0, suffix: "%" },
    { label: "Delegation %",        value: workflows?.delegation_pct || 0, suffix: "%" },
  ]), [workflows]);

  const financial   = executive?.financial   || {};
  const operational = executive?.operational || {};
  const health      = executive?.health      || {};
  const alerts      = executive?.alerts      || [];
  const timeline    = executive?.timeline    || [];

  return (
    <div className="space-y-8" data-testid="executive-dashboard">
      <PageHeader
        title="Executive Dashboard"
        subtitle="Enterprise KPIs powered by event-driven read models — no direct ledger queries."
        action={
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={refresh} disabled={loading}
                    data-testid="btn-refresh">
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button variant="outline" size="sm" onClick={rebuild} disabled={rebuilding}
                    data-testid="btn-rebuild" title="Wipe & replay projections from event history">
              <GitBranch className={`w-4 h-4 mr-1 ${rebuilding ? "animate-spin" : ""}`} />
              Rebuild projections
            </Button>
          </div>
        }
        testid="page-executive"
      />

      {/* ── Financial KPIs ─────────────────────────────────────── */}
      <Section>
        <SectionTitle hint="Live projection · updated on every posted event">Financial</SectionTitle>
        {loading ? <KpiSkeleton count={4} /> : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Metric label="Revenue Today"    value={fmtMoney(financial.revenue_today_minor)}
                    icon={TrendingUp}  tone="accent"
                    hint={`MTD ${fmtMoneyShort(financial.revenue_mtd_minor)}`}
                    testid="kpi-revenue-today" />
            <Metric label="Cash Collected"   value={fmtMoney(financial.cash_collected_today_minor)}
                    icon={Wallet}
                    hint={`Refunded ${fmtMoneyShort(financial.cash_refunded_today_minor)}`}
                    testid="kpi-cash-today" />
            <Metric label="Outstanding AR"   value={fmtMoney(financial.outstanding_revenue_minor)}
                    icon={DollarSign} tone="highlight"
                    hint={`Credit balances ${fmtMoneyShort(financial.credit_balances_minor)}`}
                    testid="kpi-outstanding" />
            <Metric label="Discounts + Refunds Today"
                    value={fmtMoney((financial.discounts_today_minor||0)+(financial.refunds_today_minor||0))}
                    icon={Percent}
                    hint={`Tax today ${fmtMoneyShort(financial.tax_today_minor)}`}
                    testid="kpi-leakage" />
          </div>
        )}
      </Section>

      {/* ── Operational KPIs ───────────────────────────────────── */}
      <Section>
        <SectionTitle hint="From rooms + reservations projections">Operations</SectionTitle>
        {loading ? <KpiSkeleton count={4} /> : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Metric label="Occupancy"      value={`${operational.occupancy_pct?.toFixed?.(1) ?? 0}%`}
                    icon={BedDouble}
                    hint={`${operational.rooms_occupied}/${operational.rooms_total} rooms`}
                    testid="kpi-occupancy" />
            <Metric label="Arrivals Today" value={fmtNum(operational.arrivals_today)}
                    icon={LogIn}
                    hint={`Departures ${operational.departures_today}`}
                    testid="kpi-arrivals" />
            <Metric label="Housekeeping Queue" value={fmtNum(operational.housekeeping_queue)}
                    icon={Activity}
                    hint={`${operational.rooms_dirty} dirty · ${operational.rooms_out_of_order} OOO`}
                    testid="kpi-hk" />
            <Metric label="Pending Checkouts" value={fmtNum(operational.pending_checkouts)}
                    icon={LogOut}
                    hint={`Business day: ${health.business_day || "—"}`}
                    testid="kpi-checkouts" />
          </div>
        )}
      </Section>

      {/* ── Health strip + Alerts ─────────────────────────────── */}
      <Section>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card padded className="lg:col-span-1">
            <div className="flex items-center gap-2 mb-3">
              <ShieldCheck className="w-4 h-4 text-hocp-textSecondary" />
              <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-hocp-textTertiary">
                Business Health
              </span>
            </div>
            <div className="space-y-3">
              <HealthRow label="Business Day"      value={health.business_day || "—"}
                          tone={health.business_day_status === "open" ? "success" : "neutral"} />
              <HealthRow label="Night Audit"       value={health.night_audit_status || "not_started"}
                          tone={health.night_audit_status === "completed" ? "success" : "warning"} />
              <HealthRow label="Fiscal Locks"      value={fmtNum(health.fiscal_locks)}       tone="info" />
              <HealthRow label="Open Workflows"    value={fmtNum(health.open_workflows)}     tone="neutral" />
              <HealthRow label="Pending Approvals" value={fmtNum(health.pending_approvals)}  tone="warning" />
            </div>
          </Card>

          <Card padded className="lg:col-span-2">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-4 h-4 text-hocp-textSecondary" />
              <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-hocp-textTertiary">
                Alerts
              </span>
            </div>
            {alerts.length === 0
              ? <EmptyState title="All clear" hint="No active alerts across finance, workflows or folios." />
              : <div className="space-y-2" data-testid="alerts-list">
                  {alerts.map((a, i) => (
                    <div key={i} className="flex items-start gap-3 rounded-lg border border-hocp-alice px-3 py-2.5"
                         data-testid={`alert-${a.kind}`}>
                      <Badge tone={a.severity === "error" ? "error" :
                                    a.severity === "warning" ? "warning" : "info"}
                             size="sm">{a.kind}</Badge>
                      <div className="flex-1 text-[13px] text-hocp-textPrimary">{a.message}</div>
                    </div>
                  ))}
                </div>}
          </Card>
        </div>
      </Section>

      {/* ── Charts ─────────────────────────────────────────────── */}
      <Section>
        <SectionTitle hint="Last 14 days · projection-sourced">Revenue &amp; Cash Trend</SectionTitle>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card padded testid="chart-revenue-trend">
            <div className="text-[13px] font-semibold mb-3">Daily Revenue vs Refunds</div>
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={revenueTrend} margin={{ top: 10, right: 8, left: -12, bottom: 0 }}>
                <defs>
                  <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={CHART.primary} stopOpacity={0.28} />
                    <stop offset="100%" stopColor={CHART.primary} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#EEF2F5" strokeDasharray="3 3" />
                <XAxis dataKey="day" tick={{ fontSize: 11, fill: "#7C8895" }} />
                <YAxis tickFormatter={fmtMoneyShort} tick={{ fontSize: 11, fill: "#7C8895" }} />
                <Tooltip content={<ChartTooltip />} />
                <Area type="monotone" dataKey="revenue" name="Revenue"
                       stroke={CHART.primary} strokeWidth={2} fill="url(#revGrad)" />
                <Line type="monotone" dataKey="refunds" name="Refunds"
                       stroke={CHART.error} strokeWidth={2} dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </Card>

          <Card padded testid="chart-cash-trend">
            <div className="text-[13px] font-semibold mb-3">Cash Collected vs Refunded</div>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={cashTrend} margin={{ top: 10, right: 8, left: -12, bottom: 0 }}>
                <CartesianGrid stroke="#EEF2F5" strokeDasharray="3 3" />
                <XAxis dataKey="day" tick={{ fontSize: 11, fill: "#7C8895" }} />
                <YAxis tickFormatter={fmtMoneyShort} tick={{ fontSize: 11, fill: "#7C8895" }} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="collected" name="Collected" fill={CHART.success} radius={[4, 4, 0, 0]} />
                <Bar dataKey="refunded"  name="Refunded"  fill={CHART.error}   radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </div>
      </Section>

      <Section>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card padded testid="chart-dept">
            <div className="text-[13px] font-semibold mb-3">Revenue by Department</div>
            {dept.length === 0
              ? <EmptyState title="No revenue by department" hint="Data appears as ChargePosted events fire." />
              : <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={dept} margin={{ top: 4, right: 8, left: -12, bottom: 0 }}>
                    <CartesianGrid stroke="#EEF2F5" strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#7C8895" }} />
                    <YAxis tickFormatter={fmtMoneyShort} tick={{ fontSize: 11, fill: "#7C8895" }} />
                    <Tooltip content={<ChartTooltip />} />
                    <Bar dataKey="value" fill={CHART.primary} radius={[4, 4, 0, 0]}>
                      {dept.map((_, i) =>
                        <Cell key={i} fill={SERIES[i % SERIES.length]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>}
          </Card>

          <Card padded testid="chart-payment-mix">
            <div className="text-[13px] font-semibold mb-3">Payment Mix</div>
            {paymentMix.length === 0
              ? <EmptyState title="No payments yet" hint="Cash trend fills in as PaymentReceived events fire." />
              : <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie data={paymentMix} dataKey="value" nameKey="name" innerRadius={54} outerRadius={90}
                          paddingAngle={2}>
                      {paymentMix.map((_, i) =>
                        <Cell key={i} fill={SERIES[i % SERIES.length]} />)}
                    </Pie>
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Tooltip content={<ChartTooltip />} />
                  </PieChart>
                </ResponsiveContainer>}
          </Card>

          <Card padded testid="chart-room-status">
            <div className="text-[13px] font-semibold mb-3">Room Status</div>
            {roomStatus.length === 0
              ? <EmptyState title="No room data" hint="Configure rooms in Inventory to see the donut." />
              : <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie data={roomStatus} dataKey="value" nameKey="name"
                          innerRadius={54} outerRadius={90} paddingAngle={2}>
                      {roomStatus.map((entry, i) =>
                        <Cell key={i} fill={entry.color} />)}
                    </Pie>
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Tooltip content={<ChartTooltip moneyMode={false} />} />
                  </PieChart>
                </ResponsiveContainer>}
          </Card>
        </div>
      </Section>

      {/* ── Workflow SLA strip ─────────────────────────────────── */}
      <Section>
        <SectionTitle hint="Governance pulse · from workflow_instances read model">
          Workflow &amp; Approval SLAs
        </SectionTitle>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {workflowMetrics.map((m, i) => (
            <Metric key={i} label={m.label}
                    value={`${m.value}${m.suffix || ""}`}
                    icon={i === 0 ? Clock : i === 1 ? AlertTriangle : i === 2 ? TrendingDown : Sparkles}
                    tone={i === 1 && m.value > 20 ? "highlight" : "default"}
                    testid={`wf-kpi-${i}`} />
          ))}
        </div>
        {workflows?.approval_bottlenecks?.length > 0 && (
          <Card padded className="mt-4" testid="chart-bottlenecks">
            <div className="text-[13px] font-semibold mb-3">Top Approval Bottlenecks</div>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={workflows.approval_bottlenecks}
                          layout="vertical"
                          margin={{ top: 4, right: 8, left: 60, bottom: 0 }}>
                <CartesianGrid stroke="#EEF2F5" strokeDasharray="3 3" />
                <XAxis type="number" tickFormatter={fmtSecs} tick={{ fontSize: 11, fill: "#7C8895" }} />
                <YAxis type="category" dataKey="state" tick={{ fontSize: 11, fill: "#7C8895" }} width={140} />
                <Tooltip content={<ChartTooltip moneyMode={false} />} />
                <Bar dataKey="median_seconds" name="Median dwell" fill={CHART.orange} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        )}
      </Section>

      {/* ── Front office boards + Activity timeline ────────────── */}
      <Section>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card padded className="lg:col-span-2" testid="front-office-panel">
            <Tabs defaultValue="arrivals" className="w-full">
              <div className="flex items-center justify-between mb-3">
                <div className="text-[13px] font-semibold">Front Office</div>
                <TabsList className="h-8">
                  <TabsTrigger value="arrivals"   className="text-[11px]" data-testid="tab-arrivals">
                    Arrivals ({frontOffice?.arrivals?.length ?? 0})
                  </TabsTrigger>
                  <TabsTrigger value="departures" className="text-[11px]" data-testid="tab-departures">
                    Departures ({frontOffice?.departures?.length ?? 0})
                  </TabsTrigger>
                  <TabsTrigger value="checkouts"  className="text-[11px]" data-testid="tab-checkouts">
                    Checkouts ({frontOffice?.pending_checkouts?.length ?? 0})
                  </TabsTrigger>
                </TabsList>
              </div>
              <TabsContent value="arrivals">
                <ReservationList items={frontOffice?.arrivals} kind="arrival"
                                 onOpen={(id) => navigate(`/pms/reservations/${id}`)} />
              </TabsContent>
              <TabsContent value="departures">
                <ReservationList items={frontOffice?.departures} kind="departure"
                                 onOpen={(id) => navigate(`/pms/reservations/${id}`)} />
              </TabsContent>
              <TabsContent value="checkouts">
                {frontOffice?.pending_checkouts?.length ? (
                  <div className="space-y-1.5">
                    {frontOffice.pending_checkouts.slice(0, 8).map(s => (
                      <button key={s.id}
                              onClick={() => navigate(`/checkout/${s.id}`)}
                              data-testid={`checkout-${s.session_no}`}
                              className="w-full flex items-center justify-between px-3 py-2 rounded-lg
                                         border border-hocp-alice hover:bg-hocp-alice/40 transition">
                        <div className="text-left">
                          <div className="text-[13px] font-medium text-hocp-textPrimary">
                            {s.session_no}
                          </div>
                          <div className="text-[11px] text-hocp-textSecondary">
                            {s.policy} · {s.status}
                          </div>
                        </div>
                        <Badge tone="info" size="sm">{s.status}</Badge>
                      </button>
                    ))}
                  </div>
                ) : <EmptyState title="No pending checkouts" />}
              </TabsContent>
            </Tabs>
          </Card>

          <Card padded testid="activity-timeline">
            <div className="flex items-center gap-2 mb-3">
              <Activity className="w-4 h-4 text-hocp-textSecondary" />
              <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-hocp-textTertiary">
                Latest Activity
              </span>
            </div>
            {timeline.length === 0
              ? <EmptyState title="No recent activity" />
              : <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
                  {timeline.map((t) => (
                    <div key={t.id} className="flex items-start gap-3 py-1.5">
                      <div className="w-1.5 h-1.5 rounded-full bg-hocp-honeydew mt-1.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-[12px] font-medium text-hocp-textPrimary truncate">
                          {t.name}
                        </div>
                        <div className="text-[10.5px] text-hocp-textTertiary tabular-nums">
                          {new Date(t.timestamp).toLocaleTimeString()} · {t.entity_type || "system"}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>}
          </Card>
        </div>
      </Section>
    </div>
  );
}

/* ── Small helpers ─────────────────────────────────────────────────── */
function HealthRow({ label, value, tone = "neutral" }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-[12px] text-hocp-textSecondary">{label}</span>
      <Badge tone={tone} size="sm">{String(value)}</Badge>
    </div>
  );
}

function ReservationList({ items = [], kind, onOpen }) {
  if (!items?.length) return <EmptyState title={`No ${kind}s today`} />;
  return (
    <div className="space-y-1.5" data-testid={`list-${kind}`}>
      {items.slice(0, 8).map(r => (
        <button key={r.id}
                onClick={() => onOpen?.(r.id)}
                data-testid={`res-${r.code}`}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg
                           border border-hocp-alice hover:bg-hocp-alice/40 transition">
          <div className="text-left">
            <div className="text-[13px] font-medium text-hocp-textPrimary flex items-center gap-2">
              {r.guest_name || r.code}
              {r.is_vip && <Award className="w-3 h-3 text-hocp-honeydew" />}
            </div>
            <div className="text-[11px] text-hocp-textSecondary">
              {r.code} {r.room_number ? `· Room ${r.room_number}` : ""}
            </div>
          </div>
          <Badge tone={r.status === "checked_in" ? "success" :
                        r.status === "checked_out" ? "neutral" : "info"} size="sm">
            {r.status}
          </Badge>
        </button>
      ))}
    </div>
  );
}
